import { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { trackCategoryVote } from '../ga';
import { FacebookFollowLink } from './FacebookFollowLink';
import './CategoryVoteModal.css';

function FacebookIcon({ size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="category-vote-modal__facebook-icon"
    >
      <path
        fill="currentColor"
        d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
      />
    </svg>
  );
}

/**
 * @param {{ categoryName: string; categorySlug: string; onClose: () => void }} props
 */
export function CategoryVoteModal({ categoryName, categorySlug, onClose }) {
  const titleId = useId();
  const [step, setStep] = useState('follow');

  useEffect(() => {
    setStep('follow');
  }, [categorySlug]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function handleFacebookClick() {
    setStep('vote');
  }

  function handleVoteClick() {
    trackCategoryVote(categorySlug);
    setStep('thanks');
  }

  return createPortal(
    <div
      className="category-vote-modal__backdrop"
      role="presentation"
      onMouseDown={(ev) => {
        if (ev.target === ev.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="category-vote-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="category-vote-modal__header">
          <h2 id={titleId} className="category-vote-modal__title">
            Vote for {categoryName}
          </h2>
          <button
            type="button"
            className="category-vote-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="category-vote-modal__body">
          {step === 'thanks' ? (
            <>
              <p
                className="category-vote-modal__thanks"
                role="status"
              >
                Thank you for your vote.
              </p>
              <div className="category-vote-modal__actions">
                <button
                  type="button"
                  className="category-vote-modal__close-btn"
                  onClick={onClose}
                >
                  Close
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="category-vote-modal__step">
                <p className="category-vote-modal__step-label">Step 1</p>
                <p className="category-vote-modal__step-text">
                  Follow us on Facebook.
                </p>
                {step === 'follow' ? (
                  <FacebookFollowLink
                    location="category_vote"
                    className="category-vote-modal__follow"
                    onClick={handleFacebookClick}
                  />
                ) : (
                  <button
                    type="button"
                    className="category-vote-modal__follow category-vote-modal__follow--done"
                    disabled
                    aria-disabled="true"
                  >
                    <FacebookIcon />
                    <span>Follow on Facebook</span>
                  </button>
                )}
              </div>

              <div
                className={`category-vote-modal__step${
                  step === 'follow' ? ' category-vote-modal__step--pending' : ''
                }`}
              >
                <p className="category-vote-modal__step-label">Step 2</p>
                <p className="category-vote-modal__step-text">
                  Confirm your vote for {categoryName}.
                </p>
                <button
                  type="button"
                  className={`category-vote-modal__vote${
                    step === 'follow' ? ' category-vote-modal__vote--pending' : ''
                  }`}
                  onClick={handleVoteClick}
                  disabled={step === 'follow'}
                  aria-disabled={step === 'follow'}
                >
                  Vote
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
